import type { ResponseErrorConfig } from "@dc/gateway-jsonapi";
import type { MutationCacheNotifyEvent } from "@tanstack/react-query";

export const TRIAL_ERROR_CODES = ["TRIAL_EXPIRED", "TRIAL_PROJECT_LIMIT_REACHED"] as const;

export type TrialErrorCode = (typeof TRIAL_ERROR_CODES)[number];

type JsonApiError = { code?: string };
type JsonApiErrors = { errors?: Array<JsonApiError> };

function isTrialErrorCode(code: string | undefined): code is TrialErrorCode {
  return !!code && (TRIAL_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * Inspect a raw error (e.g. a rejected mutation error) and return the trial code if present.
 * The API surfaces trial codes at the top-level `errors[].code` field
 * (see https://jsonapi.org/format/#error-objects — emitted by TrialAccessForbiddenException).
 * Use in per-mutation `onError` to skip generic error toasts when the trial modal will handle it.
 */
export function getTrialErrorCodeFromError(err: unknown): TrialErrorCode | null {
  const error = err as ResponseErrorConfig<JsonApiErrors> | undefined;
  const errors = error?.response?.data?.errors;
  if (!errors) return null;

  for (const entry of errors) {
    if (isTrialErrorCode(entry.code)) return entry.code;
  }

  return null;
}

export function getTrialErrorCode(event: MutationCacheNotifyEvent): TrialErrorCode | null {
  if (event.type !== "updated") return null;
  if (event.action.type !== "error") return null;
  return getTrialErrorCodeFromError(event.action.error);
}
